import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../api';
import { useLanguage } from '../context/LanguageContext';
import styles from './OAuthCallbackPage.module.css';
import Navbar from '../components/Navbar';

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Reset token is missing from URL.');
      setErrorMessage('Reset token is missing from URL.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const res = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.message || 'Failed to reset password';
        toast.error(msg);
        setErrorMessage(msg);
        setIsLoading(false);
        return;
      }
      setIsSuccess(true);
      toast.success('Password has been reset successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Server error resetting password');
      setErrorMessage('Server error resetting password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar commonNavigation className={styles.navbar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '16px 24px' }} brandClassName={styles.navBrand} brand={<><span className={styles.brandDot} />ReplyShoot</>} />

      <main className={styles.main}>
        <div className={styles.card} style={{ maxWidth: '440px', width: '100%' }}>
          {!token ? (
            <div className="text-center">
              <h2 className="h4 text-danger mb-3">{t('invalidLink', 'Invalid Password Reset Link')}</h2>
              <p className="text-muted small mb-4">
                {t('missingTokenText', 'The password reset token is missing or incomplete. Please request a new link.')}
              </p>
              <Link to="/?login=true" className="btn btn-primary w-100">
                {t('goToLogin', 'Go to Login')}
              </Link>
            </div>
          ) : isSuccess ? (
            <div className="text-center py-2">
              <div className="mb-3 text-success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" fill="#E1F5FE" stroke="#0288D1" />
                  <path d="m9 12 2 2 4-4" stroke="#0288D1" strokeWidth="2.5" />
                </svg>
              </div>
              <h2 className="h4 mb-2">{t('passwordResetSuccessTitle', 'Password Reset Successful')}</h2>
              <p className="text-muted small mb-4">
                {t('passwordResetSuccessSub', 'Your password has been reset. You can now log in with your new credentials.')}
              </p>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={() => navigate('/?login=true')}
              >
                {t('proceedToLogin', 'Proceed to Login')}
              </button>
            </div>
          ) : (
            <div>
              <h2 className="h4 text-center mb-2">{t('resetPasswordTitle', 'Create New Password')}</h2>
              <p className="text-muted small text-center mb-4">
                {t('enterNewPasswordSub', 'Enter a new password for your account below.')}
              </p>

              {errorMessage && (
                <div className="alert alert-danger small mb-3">{errorMessage}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3 position-relative">
                  <label htmlFor="newPassword" className="form-label">{t('newPasswordLabel', 'New Password')}</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    className="form-control pe-5"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-2"
                    style={{ zIndex: 5 }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? t('hide', 'Hide') : t('show', 'Show')}
                  </button>
                </div>

                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">{t('confirmPasswordLabel', 'Confirm Password')}</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    minLength={6}
                    required
                  />
                </div>

                <div className="d-grid">
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? t('saving', 'Saving...') : t('resetPasswordBtn', 'Set New Password')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
